import { TwoFactorResponse } from "@sp-crm/core";
import { LoginWorkflowLayout } from "components/app/login-workflow-layout";
import {
    TwoFactorCodeInput,
    TwoFactorMethodSelector,
    useTwoFactorFlow,
} from "components/shared/two-factor";
import { InlineBanner } from "components/ui/inline-banner";
import { TwoFactorMethod } from "generated/graphql";
import React, { FormEvent, useCallback, useEffect } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { handleLoginCompletion, LoginUserData } from "store/reducers/login";
import { ApplicationState } from "store/state";
import * as http from "util/http";
import { handleEvent } from "util/user-events";
import { Actions, navigate } from "../../store/actions";

interface SessionInfoResponse {
    sessionType: "pre2fa" | "full";
    userId?: string;
    methods?: TwoFactorMethod[];
    requiresTwoFactor?: boolean;
    method?: TwoFactorMethod;
    codeSent?: boolean;
    timezone?: string;
}

export const TwoFactorVerification: React.FC = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const loginState = useSelector((state: ApplicationState) => state.login);
    const loginFailureMessage = loginState.loginFailureMessage;
    const twoFactorData = loginState.twoFactorData as TwoFactorResponse | null;

    const {
        verificationCode,
        setVerificationCode,
        selectedMethod,
        codeSent,
        resendCountdown,
        sendCode,
        handleResendCode,
    } = useTwoFactorFlow({
        twoFactorData,
        sendCodeEndpoint: "/api/session/send-verification-code",
        sendCodePayload: method => ({
            userId: twoFactorData?.userId,
            method: method,
        }),
        onSendCodeError: () => {
            dispatch({
                type: Actions[Actions.LOGIN_FAIL],
                message:
                    "Failed to send two-factor code. Refresh and try again. If you continue to have trouble, please contact support.",
            });
        },
        eventPrefix: "2fa",
    });

    const handleMethodSelection = (method: TwoFactorMethod): void => {
        sendCode(method);
    };

    const checkSession = useCallback(async () => {
        try {
            const response = await http.get<SessionInfoResponse>(
                http.newRequestDescription(),
                "/api/session/info",
            );

            if (response.data.sessionType === "pre2fa") {
                dispatch({
                    type: Actions[Actions.LOGIN_REQUIRES_2FA],
                    twoFactorData: {
                        requiresTwoFactor: response.data.requiresTwoFactor,
                        userId: response.data.userId,
                        methods: response.data.methods,
                        method: response.data.method,
                        codeSent: response.data.codeSent,
                    },
                });
                return;
            } else if (response.data.sessionType === "full") {
                window.location.href = "/";
                return;
            }
        } catch (error) {
            navigate("/login");
        }
    }, [dispatch]);

    const handleVerificationSubmit = useCallback(
        async (e: FormEvent<HTMLFormElement>): Promise<void> => {
            e.preventDefault();
            if (!twoFactorData || !verificationCode) return;

            try {
                const response = await http.post<LoginUserData>(
                    http.newRequestDescription(),
                    "/api/verify-code",
                    {
                        code: verificationCode,
                    },
                );

                const userData = response.data;
                handleEvent("2fa-success", { method: selectedMethod });
                handleLoginCompletion(userData, dispatch, queryClient);
            } catch (error) {
                handleEvent("2fa-fail", { method: selectedMethod, step: "verify-code" });
                dispatch({
                    type: Actions[Actions.LOGIN_FAIL],
                    message:
                        typeof error?.response?.data?.err === "string"
                            ? error.response.data.err
                            : "Invalid or expired verification code. Please try again.",
                });
            }
        },
        [dispatch, queryClient, selectedMethod, twoFactorData, verificationCode],
    );

    const changeVerificationCode = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>): void => {
            setVerificationCode(e.target.value);
            if (loginFailureMessage) {
                dispatch({
                    type: Actions[Actions.LOGIN_FAIL],
                    message: null,
                });
            }
        },
        [dispatch, loginFailureMessage, setVerificationCode],
    );

    const getErrorMessage = useCallback((): JSX.Element | null => {
        if (loginFailureMessage) {
            return <InlineBanner type="error">{loginFailureMessage}</InlineBanner>;
        }
        return null;
    }, [loginFailureMessage]);

    const backToLogin = (): void => {
        navigate("/login");
    };

    useEffect(() => {
        if (!twoFactorData) {
            checkSession();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!twoFactorData) {
        return null;
    }

    return (
        <LoginWorkflowLayout title="Two-factor authentication">
            <div className="space-y-6">
                {getErrorMessage()}

                {!codeSent && twoFactorData?.methods?.length > 1 && (
                    <TwoFactorMethodSelector
                        methods={twoFactorData.methods}
                        onMethodSelect={handleMethodSelection}
                        onBack={backToLogin}
                        backButtonText="Back to login"
                    />
                )}

                {codeSent && (
                    <TwoFactorCodeInput
                        verificationCode={verificationCode}
                        onCodeChange={changeVerificationCode}
                        onSubmit={handleVerificationSubmit}
                        onResend={handleResendCode}
                        onBack={backToLogin}
                        selectedMethod={selectedMethod}
                        resendCountdown={resendCountdown}
                        backButtonText="Back to login"
                    />
                )}
            </div>
        </LoginWorkflowLayout>
    );
};
